import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const PROJECTS = [
  {
    id: "hvac",
    name: "HVAC Brand",
    tag: "Interactive Problem Finder · Estimate requests",
    img: "https://images.unsplash.com/photo-1777253988341-2bfc8e097a04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxodmFjJTIwaW5kdXN0cmlhbCUyMGJ1aWxkaW5nJTIwZGFya3xlbnwwfHx8fDE3OTAyODU1MTZ8MA&ixlib=rb-4.1.0&q=85",
    fragment: "finder",
  },
  {
    id: "handled",
    name: "HANDLED Property Co.",
    tag: "Property services · Accounts & checkout · Property watch",
    img: "https://images.pexels.com/photos/4948974/pexels-photo-4948974.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    fragment: "watch",
  },
  {
    id: "construction",
    name: "Construction Brand",
    tag: "Photo estimates · Project inquiries",
    img: "https://images.unsplash.com/photo-1518112166137-85f9979a43aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwYXJjaGl0ZWN0dXJhbCUyMGNvbnN0cnVjdGlvbiUyMGRhcmt8ZW58MHx8fHwxNzkwMjg1NTIwfDA&ixlib=rb-4.1.0&q=85",
    fragment: "estimate",
  },
];

function FinderFragment() {
  const steps = ["No cooling", "Weak airflow", "Thermostat dead", "Strange noise"];
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % steps.length), 2200);
    return () => clearInterval(id);
  }, [steps.length]);
  return (
    <div data-testid="fragment-finder" className="w-52 border border-white/15 bg-obsidian/70 p-4 backdrop-blur-md">
      <p className="text-[9px] uppercase tracking-[0.3em] text-fog">Problem Finder</p>
      <div className="mt-3 h-5 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.p
            key={steps[i]}
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -16, opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="text-sm font-medium"
          >
            {steps[i]}
          </motion.p>
        </AnimatePresence>
      </div>
      <div className="mt-3 h-px w-full bg-white/15">
        <motion.div
          className="h-full bg-white"
          animate={{ width: `${((i + 1) / steps.length) * 100}%` }}
          transition={{ duration: 0.6, ease: EASE }}
        />
      </div>
    </div>
  );
}

function WatchFragment() {
  const [on, setOn] = useState(true);
  useEffect(() => {
    const id = setInterval(() => setOn((v) => !v), 2600);
    return () => clearInterval(id);
  }, []);
  return (
    <div data-testid="fragment-watch" className="w-52 border border-white/15 bg-obsidian/70 p-4 backdrop-blur-md">
      <p className="text-[9px] uppercase tracking-[0.3em] text-fog">Property Watch</p>
      <div className="mt-3 flex items-center gap-2">
        <span
          className="h-1.5 w-1.5 rounded-full bg-emerald-400"
          style={{ animation: "pulse-dot 1.6s ease-in-out infinite" }}
        />
        <span className="text-sm font-medium">Active</span>
      </div>
      <div className="mt-2 h-4 overflow-hidden text-xs text-fog">
        <AnimatePresence mode="wait">
          <motion.span
            key={on ? "a" : "b"}
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -12, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="block"
          >
            {on ? "Visit logged — 09:41" : "Invoice #1042 — Paid"}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}

function EstimateFragment() {
  return (
    <div data-testid="fragment-estimate" className="w-52 border border-white/15 bg-obsidian/70 p-4 backdrop-blur-md">
      <p className="text-[9px] uppercase tracking-[0.3em] text-fog">Photo Estimate</p>
      <div className="relative mt-3 h-10 overflow-hidden border border-dashed border-white/25">
        <div
          className="absolute inset-y-0 w-1/2 bg-white/10"
          style={{ animation: "shimmer-slide 2.4s ease-in-out infinite" }}
        />
        <p className="absolute inset-0 flex items-center justify-center text-[10px] text-fog">
          Drop photos of the job
        </p>
      </div>
      <p className="mt-2 text-xs text-fog">+3 photos received</p>
    </div>
  );
}

const FRAGMENTS = { finder: FinderFragment, watch: WatchFragment, estimate: EstimateFragment };

function GlimpseItem({ project, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const reversed = index % 2 === 1;
  const Fragment = FRAGMENTS[project.fragment];

  return (
    <div
      ref={ref}
      data-testid={`glimpse-${project.id}`}
      className="grid items-center gap-8 md:grid-cols-12 md:gap-12"
    >
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-12%" }}
        transition={{ duration: 1, ease: EASE }}
        className={`relative aspect-[16/10] overflow-hidden md:col-span-7 ${reversed ? "md:order-2" : ""}`}
      >
        <motion.img
          src={project.img}
          alt={`${project.name} — project glimpse`}
          loading="lazy"
          initial={{ scale: 1.3 }}
          whileInView={{ scale: 1.18 }}
          viewport={{ once: true, margin: "-12%" }}
          transition={{ duration: 1.4, ease: EASE }}
          style={{ y: imgY }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <motion.div
          aria-hidden="true"
          initial={{ scaleY: 1 }}
          whileInView={{ scaleY: 0 }}
          viewport={{ once: true, margin: "-12%" }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.1 }}
          className="absolute inset-0 z-10 origin-top bg-obsidian"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-obsidian/20" />
        <div className="absolute bottom-4 left-4 z-20">
          <Fragment />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-12%" }}
        transition={{ duration: 1, delay: 0.2, ease: EASE }}
        className={`md:col-span-5 ${reversed ? "md:order-1 md:text-right" : ""}`}
      >
        <span className="text-xs tracking-[0.3em] text-fog">{String(index + 1).padStart(2, "0")}</span>
        <h3 className="mt-3 font-display text-2xl font-medium tracking-[-0.02em] md:text-4xl">
          {project.name}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-fog">{project.tag}</p>
      </motion.div>
    </div>
  );
}

export default function WorkGlimpses() {
  return (
    <section data-testid="work-glimpses-section" className="relative px-5 py-24 md:px-16 md:py-40">
      <div className="mx-auto max-w-[1500px]">
        <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">
          Selected work
        </p>
        <motion.h2
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1, ease: EASE }}
          className="mt-6 font-display text-5xl font-medium tracking-[-0.03em] md:text-8xl"
        >
          See what we&rsquo;ve built.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="mt-6 max-w-xl text-sm leading-relaxed text-fog md:text-base"
        >
          Real websites. Real functionality. Explore the details, experience the systems, and see
          what&rsquo;s possible for your business.
        </motion.p>

        <div className="mt-20 flex flex-col gap-24 md:mt-32 md:gap-40">
          {PROJECTS.map((p, i) => (
            <GlimpseItem key={p.id} project={p} index={i} />
          ))}
        </div>

        <div className="mt-24 flex flex-col gap-6 border-t border-white/10 pt-10 md:flex-row md:items-center md:justify-end">
          <Link
            to="/work"
            data-testid="glimpses-work-link"
            className="group flex items-center gap-3 font-display text-lg font-medium md:text-xl"
          >
            <span className="border-b border-white/30 pb-1 transition-colors duration-500 group-hover:border-white">
              Explore Our Work
            </span>
            <ArrowUpRight className="h-5 w-5 transition-transform duration-500 ease-cinematic group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
